import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community'
import { useMemo } from 'react'
import { fetchClient } from '../../../../api/typedClient'
import { getGridEndpoint } from '../utils'

interface UseServerSideDataSourceProps {
  entityType: string
  onTotalRowsChange?: (total: number) => void
  searchTerm?: string
  defaultPageSize?: number
}

interface UseServerSideDataSourceReturn {
  serverSideDatasource: IServerSideDatasource
}

/**
 * Hook for creating server-side data source for AG Grid
 */
export const useServerSideDataSource = ({
  entityType,
  onTotalRowsChange,
  searchTerm,
  defaultPageSize,
}: UseServerSideDataSourceProps): UseServerSideDataSourceReturn => {
  const serverSideDatasource = useMemo<IServerSideDatasource>(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        try {
          const startRow = params.request.startRow ?? 0
          const endRow = params.request.endRow ?? startRow

          // Build query parameters
          const queryParams = new URLSearchParams()

          // Pagination
          queryParams.set('skip', startRow.toString())
          const requestedLimit = endRow - startRow

          const effectiveLimit =
            requestedLimit > 0 ? requestedLimit : defaultPageSize || 500

          queryParams.set('limit', String(effectiveLimit))

          // Sorting
          if (params.request.sortModel && params.request.sortModel.length > 0) {
            const sortModel = params.request.sortModel[0]
            const sortDirection = sortModel.sort === 'asc' ? '' : '-'
            queryParams.set('ordering', `${sortDirection}${sortModel.colId}`)
          }

          // Filtering
          if (params.request.filterModel) {
            Object.entries(params.request.filterModel).forEach(
              ([field, filter]) => {
                if (filter && typeof filter === 'object') {
                  // Handle different filter types
                  if ('filter' in filter && filter.filter) {
                    queryParams.set(`${field}__icontains`, filter.filter)
                  } else if ('dateFrom' in filter || 'dateTo' in filter) {
                    if (filter.dateFrom) {
                      queryParams.set(`${field}__gte`, filter.dateFrom)
                    }
                    if (filter.dateTo) {
                      queryParams.set(`${field}__lte`, filter.dateTo)
                    }
                  }
                }
              },
            )
          }

          // Global search support. Prefer the explicitly managed searchTerm
          // from the control bar wiring; fall back to any quick/global value
          // that might be present on the request (not guaranteed with SSRM).
          const reqExtra = params.request as unknown as {
            globalFilter?: unknown
            quickFilter?: unknown
          }
          const globalSearch =
            searchTerm || reqExtra.globalFilter || reqExtra.quickFilter
          if (globalSearch) {
            // All grid endpoints use 'search_term' parameter
            queryParams.set('search_term', String(globalSearch))
          }

          // Make API request
          const endpoint = getGridEndpoint(entityType)

          // Make API request using fetchClient
          const { data: response, error: fetchError } = await fetchClient.GET(
            // @ts-expect-error TODO: Dynamic grid endpoints not in OpenAPI spec
            endpoint,
            {
              params: {
                query: Object.fromEntries(queryParams.entries()),
              },
              headers: {
                'X-Tenant-Name': 'Gravitate', // Master data uses Gravitate tenant
              },
            },
          )

          if (fetchError) {
            console.error('Failed to fetch data:', fetchError)
            params.fail()
            return
          }

          // gravitateTenantClient returns parsed JSON directly. Handle the two
          // common shapes:
          // 1) Paginated object: { count, results: [...] }
          // 2) Array response: [...]
          // Optionally some endpoints may use { total, data/items }.
          let data: unknown[] = []
          let total = 0

          if (
            response &&
            typeof response === 'object' &&
            !Array.isArray(response)
          ) {
            // Try to detect paginator shapes
            const respObj = response as Record<string, unknown>
            // Prefer new grid shape: rows + num_results/num_pages
            const numResults =
              typeof respObj.num_results === 'number'
                ? respObj.num_results
                : null
            if (Array.isArray((respObj as Record<string, unknown>).rows)) {
              const rows = (respObj as Record<string, unknown>)
                .rows as unknown[]
              data = rows
              // Use num_results if it's a positive number, otherwise use rows.length
              total =
                numResults != null && numResults > 0 ? numResults : rows.length
              // Ensure num_pages null/undefined treated as 1 (already defaulted above)
            } else if (
              Array.isArray((respObj as Record<string, unknown>).results)
            ) {
              // Legacy paginator shapes
              const results = (respObj as Record<string, unknown>)
                .results as unknown[]
              data = results
              total =
                typeof (respObj as Record<string, unknown>).count === 'number'
                  ? ((respObj as Record<string, unknown>).count as number)
                  : typeof (respObj as Record<string, unknown>).total ===
                      'number'
                    ? ((respObj as Record<string, unknown>).total as number)
                    : results.length
            } else if (
              (respObj as Record<string, unknown>).data &&
              typeof (respObj as Record<string, unknown>).data === 'object'
            ) {
              // Handle nested data shape: { data: { results, count } }
              const inner = (respObj as Record<string, unknown>).data as Record<
                string,
                unknown
              >
              if (Array.isArray(inner.results as unknown[])) {
                const innerResults = inner.results as unknown[]
                data = innerResults
                total =
                  typeof inner.count === 'number'
                    ? (inner.count as number)
                    : typeof inner.total === 'number'
                      ? (inner.total as number)
                      : innerResults.length
              } else if (Array.isArray(inner.items as unknown[])) {
                const items = inner.items as unknown[]
                data = items
                total =
                  typeof inner.total === 'number'
                    ? (inner.total as number)
                    : items.length
              } else if (Array.isArray(inner.data as unknown[])) {
                const innerData = inner.data as unknown[]
                data = innerData
                total =
                  typeof inner.total === 'number'
                    ? (inner.total as number)
                    : innerData.length
              }
            } else if (
              Array.isArray((respObj as Record<string, unknown>).items)
            ) {
              const items = (respObj as Record<string, unknown>)
                .items as unknown[]
              data = items
              total =
                typeof (respObj as Record<string, unknown>).total === 'number'
                  ? ((respObj as Record<string, unknown>).total as number)
                  : items.length
            } else if (
              Array.isArray((respObj as Record<string, unknown>).data)
            ) {
              const d = (respObj as Record<string, unknown>).data as unknown[]
              data = d
              total =
                typeof (respObj as Record<string, unknown>).total === 'number'
                  ? ((respObj as Record<string, unknown>).total as number)
                  : d.length
            } else {
              // Fallback: unknown object shape, try to treat as array-ish values
              data = []
              total = 0
            }
          } else if (Array.isArray(response)) {
            // For companies endpoint which returns a flat array
            data = response as unknown[]
            total = data.length // Get total before slicing
            // Apply client-side pagination for array responses
            const start = params.request.startRow || 0
            const end = params.request.endRow || data.length
            data = data.slice(start, end)
          }

          // If total is not provided by API and looks equal to page size on the
          // first page, attempt a lightweight follow-up request to detect a
          // `count`/`total` field. This avoids doubling requests on every page.
          const inferredPageSize =
            typeof params.request.endRow === 'number' &&
            typeof params.request.startRow === 'number'
              ? params.request.endRow - params.request.startRow
              : defaultPageSize || effectiveLimit

          if (
            (typeof total !== 'number' || total <= data.length) &&
            Array.isArray(data) &&
            data.length === inferredPageSize &&
            startRow === 0
          ) {
            try {
              const countQueryParams = Object.fromEntries(queryParams.entries())
              countQueryParams.limit = '1'
              countQueryParams.offset = '0'
              const { data: countResp } = await fetchClient.GET(
                // @ts-expect-error TODO: Dynamic grid endpoints not in OpenAPI spec
                endpoint,
                {
                  params: {
                    query: countQueryParams,
                  },
                  headers: {
                    'X-Tenant-Name': 'Gravitate',
                  },
                },
              )
              if (
                countResp &&
                typeof countResp === 'object' &&
                !Array.isArray(countResp)
              ) {
                const obj = countResp as Record<string, unknown>
                const inferred =
                  (typeof obj.count === 'number' && obj.count) ||
                  ((obj.data as Record<string, unknown>) &&
                    typeof (obj.data as Record<string, unknown>).count ===
                      'number' &&
                    (obj.data as Record<string, unknown>).count) ||
                  (typeof obj.total === 'number' && obj.total)
                if (typeof inferred === 'number' && inferred > total) {
                  total = inferred
                }
              }
            } catch (_e) {
              // Best-effort: ignore failures and proceed with current total
            }
          }

          // Update total rows callback
          if (onTotalRowsChange && typeof total === 'number') {
            onTotalRowsChange(total)
          }

          // Success callback
          params.success({
            rowData: data,
            rowCount: total,
          })
        } catch (error) {
          console.error('Failed to fetch data:', error)
          params.fail()
        }
      },
    }),
    [entityType, onTotalRowsChange, searchTerm, defaultPageSize],
  )

  return {
    serverSideDatasource,
  }
}
